/**
 * UUID Regex Validator
 *
 * Centralized validation for RFC 4122 compliant UUIDs.
 * Validates version and variant bits to ensure proper UUID format.
 *
 * @example
 * import { UuidRegex } from '@nexus/types';
 *
 * // Validate
 * if (UuidRegex.test(input)) {
 *   const uuid = UuidRegex.parse(input);
 * }
 *
 * // Check the pattern
 * const pattern = UuidRegex.pattern;
 */

import { BaseRegexValidator } from "./base-regex-validator";

/**
 * Branded type for validated UUIDs
 * Prevents mixing regular strings with validated UUIDs
 */
export type Uuid = string & { readonly __brand: "Uuid" };

/**
 * UUID regex validator implementation
 *
 * RFC 4122 compliant UUID validation:
 * - Format: 8-4-4-4-12 hex digits separated by hyphens
 * - Version: 1-5 (validates position 14)
 * - Variant: RFC 4122 variant (validates position 19: 8, 9, a, or b)
 *
 * This ensures:
 * - Valid UUID structure
 * - Correct version bits
 * - Correct variant bits
 * - Rejects malformed UUIDs like 00000000-0000-0000-0000-000000000000
 */
class UuidRegexValidator extends BaseRegexValidator<Uuid> {
  constructor() {
    super({
      // RFC 4122 UUID pattern
      // Position 14 (version): [1-5] - only UUID versions 1-5
      // Position 19 (variant): [89ab] - RFC 4122 variant bits
      pattern: /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
      description: "RFC 4122 compliant UUID (version 1-5 with proper variant bits)",
      examples: [
        "550e8400-e29b-41d4-a716-446655440000",
        "6ba7b810-9dad-11d1-80b4-00c04fd430c8",
        "a0eebc99-9c0b-4ef8-bb6d-6bb9bd380a11"
      ] as const,
      errorMessage:
        "Invalid UUID format. Expected RFC 4122 compliant UUID (e.g., 550e8400-e29b-41d4-a716-446655440000)",
      transform: (value) => value as Uuid
    });
  }

  /**
   * Extract the UUID version (1-5)
   * Returns null if not a valid UUID
   */
  getVersion(uuid: string): number | null {
    if (!this.test(uuid)) return null;
    return Number.parseInt(uuid.charAt(14), 16);
  }

  /**
   * Check if UUID is version 4 (random)
   */
  isV4(uuid: string): boolean {
    return this.getVersion(uuid) === 4;
  }
}

/**
 * Singleton instance - use this for all UUID validation
 */
export const UuidRegex = new UuidRegexValidator();
